import { cn } from "../../lib/utils";
import { useTranslation } from "react-i18next";
import { NavItem } from "../core/NavItem";
import { useParams, useNavigate } from "react-router-dom";
import { ROLES } from "@/common/permissions";
import { ButtonWithPermission } from "../core/ButtonWithPermission";

interface BottomNavProps {
  showChargeButton?: boolean;
  className?: string;
}

const navButtonStyles = cn(
  "w-1/5 h-full rounded-none",
  "hover:bg-dark-foreground/10"
);

export function BottomNav({
  showChargeButton = true,
  className,
}: BottomNavProps) {
  const { t } = useTranslation();
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();

  const handleChargeClick = () => {
    if (memberId) {
      // If we're on a member's page, navigate to the charge page
      navigate(`/members/${memberId}/charge`);
    } else {
      // If we're not on a member's page, navigate to members page with state
      navigate("/members", { state: { fromChargeButton: true } });
    }
  };

  return (
    <nav
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50",
        "h-20 bg-dark border-t border-dark/10",
        "flex items-center justify-between",
        "safe-area-inset-bottom pb-safe",
        className
      )}
    >
      {/* Left Actions */}
      <NavItem
        to="/orders"
        label={t("mainActions.orders")}
        className={navButtonStyles}
      />
      <NavItem
        to="/members"
        label={t("mainActions.members")}
        className={navButtonStyles}
        role={ROLES.MANAGER}
      />

      {/* Center Charge Button */}
      {showChargeButton && (
        <div className="flex items-center justify-center w-1/5 -translate-y-10">
          <ButtonWithPermission
            size="lg"
            onClick={handleChargeClick}
            className={cn(
              "w-20 h-20 rounded-full border-2 border-white",
              "shadow-lg hover:shadow-xl",
              "transform hover:scale-105 active:scale-95",
              "transition-all duration-200"
            )}
            role={ROLES.MANAGER}
          >
            <span className="text-sm font-medium">
              {t("mainActions.charge")}
            </span>
          </ButtonWithPermission>
        </div>
      )}

      <NavItem
        to="/team"
        label={t("mainActions.team")}
        className={navButtonStyles}
        role={ROLES.MANAGER}
      />
      <NavItem
        to="/reports"
        label={t("mainActions.reports")}
        className={navButtonStyles}
        role={ROLES.MANAGER}
      />
    </nav>
  );
}
